import React, { useState } from 'react';
import { TABLET_PORTRAIT, DESKTOP } from 'src/client/constants/mediaQueries';
import { useSelector } from 'src/client/redux';
import styled from 'styled-components/macro';
import { BrandedPillButton, PillButton, TEXT_SHADOW_LARGE, TEXT_SHADOW_SMALL } from './styles';
import ReactSlick, { Settings } from 'react-slick';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { useShowSfwSite } from 'src/client/helpers/useFeatureFlags';
import { StaticSplashResponse } from 'src/@types/StaticSplashResponse';
import { FreeGalleryEmail } from './FreeGalleryEmail';

type Props = {
  freeGalleries?: StaticSplashResponse['freeGalleries'];
};

const nextArrow = (
  <PillButton>
    <FontAwesomeIcon icon={faChevronRight} />
  </PillButton>
);

const prevArrow = (
  <PillButton>
    <FontAwesomeIcon icon={faChevronLeft} />
  </PillButton>
);

const sliderSettings: Settings = {
  arrows: true,
  autoplay: false,
  autoplaySpeed: 5000,
  dots: false,
  fade: true,
  infinite: true,
  nextArrow,
  prevArrow,
  slidesToScroll: 1,
  slidesToShow: 1,
  speed: 1000,
};

export const FreeGallerySlider = (props: Props) => {
  const { slides = [], buttonText = '' } = props.freeGalleries || {};

  const [showEmailSection, setShowEmailSection] = useState(false);
  const { cdnUrl } = useSelector((state) => state.app);
  const site = useSelector((state) => state.site);
  const showSfwSite = useShowSfwSite();
  const nude = showSfwSite ? 'nonNude' : 'nude';

  if (!props.freeGalleries || showSfwSite) {
    return null;
  }

  const makeBackgroundUrl = (image: string) => {
    return `${cdnUrl}/staticSplash/${site.UUID}/freeGallery/${nude}/${image}`;
  };

  return (
    <Wrapper showEmailSection={showEmailSection}>
      <ReactSlick {...sliderSettings}>
        {slides.map((gallery) => (
          <Slide
            key={gallery.title}
            mobileBackgroundImage={makeBackgroundUrl(gallery.mobileImage)}
            desktopBackgroundImage={makeBackgroundUrl(gallery.desktopImage)}
          >
            <TextContainer hidden={showEmailSection}>
              <Title>{gallery.title}</Title>
              <Starring>
                Starring: <b>{gallery.modelName}</b>
              </Starring>
              <FreeGalleryButton onClick={() => setShowEmailSection(true)}>{buttonText}</FreeGalleryButton>
            </TextContainer>
          </Slide>
        ))}
      </ReactSlick>
      {showEmailSection && <FreeGalleryEmail setShowEmailSection={setShowEmailSection} />}
    </Wrapper>
  );
};

const Wrapper = styled.section<{ showEmailSection?: boolean }>`
  --aspect-ratio: 360 / 320;
  background-color: ${(props) => props.theme.headerBarPrimary5};
  grid-area: free-gallery;
  position: relative;
  overflow: hidden;
  aspect-ratio: var(--aspect-ratio);

  @media ${TABLET_PORTRAIT} {
    --aspect-ratio: 768 / 246;
    align-items: flex-start;
    order: 2;
  }

  @media ${DESKTOP} {
    --aspect-ratio: 2560 / 720;
  }

  .slick-slider {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .slick-arrow {
    z-index: 10;
    margin: 0 5px;
    font-size: 20px;
    cursor: pointer;
    opacity: 0.7;
    background: none !important;

    // !important because the slider puts display block on the element in the dom
    display: ${(props) => (props.showEmailSection ? 'none' : 'flex')} !important;

    &:hover {
      opacity: 1;
      background: none !important;
    }

    svg {
      filter: drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.75));
    }
  }

  .slick-next {
    right: 0;
  }
`;

const Slide = styled.div<{ mobileBackgroundImage: string; desktopBackgroundImage: string }>`
  display: inline-flex !important;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;

  aspect-ratio: var(--aspect-ratio);
  background-image: url(${(props) => props.mobileBackgroundImage});

  @media ${TABLET_PORTRAIT} {
    align-items: flex-start;
    background-image: url(${(props) => props.desktopBackgroundImage});
  }
`;

const TextContainer = styled.div<{ hidden: boolean }>`
  display: ${(props) => (props.hidden ? 'none' : 'flex')};
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media ${TABLET_PORTRAIT} {
    width: 50%;
  }
`;

const Title = styled.span`
  font-size: 32px;
  font-weight: 800;
  color: #fff;
  text-align: center;
  ${TEXT_SHADOW_LARGE};

  @media ${TABLET_PORTRAIT} {
    font-size: 28px;
  }

  @media ${DESKTOP} {
    font-size: 52px;
  }
`;

const Starring = styled.span`
  font-size: 14px;
  color: #fff;
  text-align: center;
  margin-bottom: 10px;
  ${TEXT_SHADOW_SMALL}

  b {
    font-weight: 600;
  }

  @media ${DESKTOP} {
    font-size: 18px;
  }
`;

export const FreeGalleryButton = styled(BrandedPillButton)`
  --height: 48px;
  font-size: 18px;

  @media ${TABLET_PORTRAIT} {
    --height: 36px;
    font-size: 14px;
  }

  @media ${DESKTOP} {
    --height: 68px;
    font-size: 24px;
  }
`;
